import axios, { AxiosResponse } from 'axios';
import { LoginRequest, LoginResponse, User, ApiResponse } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('teaam_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('teaam_token');
      localStorage.removeItem('teaam_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', credentials);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Login failed');
      }
      
      return response.data.data;
    } catch (error: any) {
      // Network error - couldn't reach backend
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED' || !error.response) {
        throw new Error('Unable to connect to server. Please check your connection and try again.');
      }
      
      // Server responded with error
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      // Rate limit error
      if (error.response?.data?.error?.includes('Too many')) {
        throw new Error(error.response.data.error);
      }
      
      // Default error
      throw new Error('Login failed. Please check your credentials.');
    }
  },

  async getProfile(token?: string): Promise<User> {
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await apiClient.get<ApiResponse<User>>('/auth/profile', config);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch profile');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to fetch user profile');
    }
  },

  async updateProfile(profileData: Partial<User>): Promise<User> {
    try {
      const response = await apiClient.put<ApiResponse<User>>('/auth/profile', profileData);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to update profile');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update profile');
    }
  },

  async changePassword(passwordData: { currentPassword: string; newPassword: string }): Promise<void> {
    try {
      const response = await apiClient.put<ApiResponse<void>>('/auth/password', passwordData);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to change password');
      }
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to change password');
    }
  },

  async refreshToken(token: string): Promise<{ token: string; expiresIn: number }> {
    try {
      const response = await apiClient.post<ApiResponse<{ token: string; expiresIn: number }>>(
        '/auth/refresh',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to refresh token');
      }
      
      return response.data.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to refresh token');
    }
  },

  async logout(token: string): Promise<void> {
    try {
      await apiClient.post<ApiResponse<void>>(
        '/auth/logout',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Ignore logout errors as they're not critical
      console.warn('Logout request failed:', error);
    }
  },
};

export default authService;